// Diamond packs sold for real money (server/store.ts takes payment through Stripe Checkout).
// Bought diamonds follow the same rules as every other diamond: account-bound, never
// tradable, never sold for coins, and whatever they buy stays on the account.

export interface DiamondPack { id: string; diamonds: number; cents: number }

/** Base price per diamond in US cents; bigger packs are priced below it. */
export const BASE_CENTS_PER_DIAMOND = 8;
/** Packs at or above this discount show it as a badge. */
export const DISCOUNT_BADGE_MIN = 10;
/** Unspent bought diamonds can be refunded within this many days. */
export const REFUND_DAYS = 7;

export const DIAMOND_PACKS: DiamondPack[] = [
  { id: 'd60', diamonds: 60, cents: 499 },
  { id: 'd130', diamonds: 130, cents: 999 },
  { id: 'd350', diamonds: 350, cents: 2499 },
  { id: 'd1000', diamonds: 1000, cents: 6599 },
  { id: 'd2000', diamonds: 2000, cents: 12299 },
];

/**
 * The Starter Pack: real money, once per account (owner's call, 2026-10-14). No diamonds: the season
 * pass is bought on its own. The outfit, the boost time, the seals and potions (never tradeable
 * items) stay on the account; the coins are ordinary coins. No coin is bound (Cole 2026-10-20: binding
 * is too confusing; he accepts that, once gems are on, gems → diamonds → coins is a route to coins).
 * Nothing caught with its seals is marked.
 *
 * Coins: about 2.5 hours of a Lv 10-30 trainer's kill coins. Their hunts pay 144-278 coins a kill
 * (shared/xp.ts killCoins, hunt Lv 10-30) at 350 kills an hour at level and up to ~1,000 a little
 * below it (sim/README.md): about 120,000 an hour at Lv 20, so 2.5 hours is 300,000.
 */
export const STARTER = {
  id: 'starter',
  cents: 299,
  coins: 300_000,
  /** Trainer XP boost (shared/boosts.ts 'txp'), hours. */
  boostHours: 72,
  /** Healing potions, the tier for the trainer's best hunt when granted (shared/quests.ts potionFor). */
  potions: 50,
  /** The best seal the merchant sells for coins. */
  seal: 'mythril-seal',
  seals: 1000,
  outfit: 'starter',
} as const;

/** Whether the Diamond packs tab shows the Starter Pack card: the store is on and this account has not bought it. */
export const starterOffered = (info: StoreInfo | null | undefined, t: { starterPack?: number } | null | undefined) => !!info?.enabled && !!info.starter && !!t && !t.starterPack;

/** The Starter Pack's contents as short lines, for the shop card and the purchase panel. */
export function starterLines(potionName = 'Healing potions', sealName = 'Mythril Seals', outfitName = 'Starter outfit'): string[] {
  return [
    `${(STARTER.boostHours / 24).toFixed(0)} days Trainer XP boost`,
    `${STARTER.coins.toLocaleString('en-US')} coins`,
    `${STARTER.seals.toLocaleString('en-US')} ${sealName}`,
    `${STARTER.potions} ${potionName}`,
    `Outfit: ${outfitName}`,
  ];
}

export function packById(id: unknown): DiamondPack | undefined {
  return typeof id === 'string' ? DIAMOND_PACKS.find((p) => p.id === id) : undefined;
}

/** Whole percent below the base price per diamond (0 when at or above it). */
export function packDiscount(p: DiamondPack): number {
  const base = p.diamonds * BASE_CENTS_PER_DIAMOND;
  return Math.max(0, Math.round((1 - p.cents / base) * 100));
}

export const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/** off: no keys (or a live key without permission); test: Stripe test mode; live: real charges. */
export type StoreMode = 'off' | 'test' | 'live';

/** GET /api/store */
export interface StoreInfo { enabled: boolean; mode: StoreMode; packs: DiamondPack[]; refundDays: number; starter?: { id: string; cents: number } }

/** Recent orders for the admin page. */
export interface OrderRow { id: string; account: number; name: string; pack: string; diamonds: number; cents: number; status: string; created: number; paid: number | null; clawed: number }
/** liveStaged: live Stripe keys are set on the server; liveOn: the admin's real-money switch. */
export interface StoreAdmin { mode: StoreMode; orders: OrderRow[]; liveStaged?: boolean; liveOn?: boolean }

/** Coins that may go to another player (market buys, gifts): all but admin-granted coins still held. */
export const tradableCoins = (t: { coins: number; devCoins?: number }) => Math.max(0, t.coins - Math.min(t.devCoins ?? 0, t.coins));
